import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  IconButton,
  TableCell,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { CellEditProps, EnhancedTableColumn } from '../../types';

interface InlineEditableCellProps<T> {
  value: unknown;
  row: T;
  column: EnhancedTableColumn<T>;
  columnWidth?: number;
  tableLayout?: 'auto' | 'fixed';
  isEditing: boolean;
  onStartEdit: () => void;
  onCommit: (newValue: unknown) => void;
  onCancel: () => void;
  triggerMode: 'click' | 'doubleClick' | 'manual';
  isFocused?: boolean;
  onCellFocus?: () => void;
}

export function InlineEditableCell<T>({
  value,
  row,
  column,
  columnWidth,
  tableLayout = 'auto',
  isEditing,
  onStartEdit,
  onCommit,
  onCancel,
  triggerMode,
  isFocused = false,
  onCellFocus,
}: InlineEditableCellProps<T>) {
  const [editValue, setEditValue] = useState<string>(String(value || ''));
  const inputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLTableCellElement>(null);
  const theme = useTheme();

  const wasEditingRef = useRef(false);

  // Manage DOM focus based on keyboard navigation focus
  useEffect(() => {
    if (isFocused && cellRef.current && !isEditing) {
      cellRef.current.focus();
    }
    // Don't blur when not focused - let natural focus management handle it
  }, [isFocused, isEditing]);

  useEffect(() => {
    // Only update editValue when transitioning from not editing to editing
    if (isEditing && !wasEditingRef.current) {
      const initialValue = String(value || '');

      setEditValue(initialValue);
      // Focus the input after a short delay to ensure it's rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Position cursor at end
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 100); // Longer delay to ensure TextField is fully rendered
    }

    wasEditingRef.current = isEditing;
  }, [isEditing, value]);

  // Handle external value changes when not editing
  useEffect(() => {
    if (!isEditing) {
      const newValue = String(value || '');

      setEditValue(newValue);
    }
  }, [value, isEditing]);

  const handleCellClick = (event: React.MouseEvent) => {
    // Prevent the click from bubbling up to the row (which might select it)
    event.stopPropagation();

    // Cell click logging removed - functionality confirmed working

    // Always focus the cell when clicked
    onCellFocus?.();

    // Also ensure DOM focus for immediate visual feedback
    if (cellRef.current) {
      cellRef.current.focus();
    }

    if (column.editable && triggerMode === 'click' && !isEditing) {
      onStartEdit();
    }
  };

  const handleCellDoubleClick = (event: React.MouseEvent) => {
    // Prevent the double-click from bubbling up to the row
    event.stopPropagation();

    // Focus cell on double click as well
    onCellFocus?.();

    if (column.editable && triggerMode === 'doubleClick' && !isEditing) {
      onStartEdit();
    }
  };

  const handleCommit = () => {
    const finalValue = convertValue(editValue, column);
    onCommit(finalValue);
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // Don't commit if clicking on the input, buttons, or the TextField container
    const target = event.target as Element;
    if (
      target.closest('input') ||
      target.closest('button') ||
      target.closest('.MuiTextField-root') ||
      target.closest('[role="textbox"]')
    ) {
      return;
    }
    handleCommit();
  };

  const handleCancel = () => {
    setEditValue(String(value || ''));
    onCancel();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCommit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  const convertValue = (strValue: string, _column: EnhancedTableColumn<T>) => {
    // Try to infer the type from the original value
    if (typeof value === 'number') {
      const numValue = Number(strValue);
      return isNaN(numValue) ? value : numValue;
    }
    if (typeof value === 'boolean') {
      return strValue.toLowerCase() === 'true';
    }
    return strValue;
  };

  // Calculate width the same way as header cells
  // Fallback behavior depends on tableLayout:
  // - tableLayout: 'fixed': undefined allows columns to share remaining space
  // - tableLayout: 'auto' (default): 120px fallback for backward compatibility
  const cellWidth =
    columnWidth ?? column.width ?? (tableLayout === 'fixed' ? undefined : 120);

  if (!column.editable) {
    // Non-editable cell
    return (
      <TableCell
        ref={cellRef}
        onClick={handleCellClick}
        onDoubleClick={handleCellDoubleClick}
        tabIndex={-1}
        sx={{
          minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
          width: cellWidth ? `${cellWidth}px` : undefined,
          maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
          boxSizing: 'border-box',
        }}
      >
        {column.format ? column.format(value, row) : String(value || '')}
      </TableCell>
    );
  }

  if (isEditing) {
    // Editing mode
    const editProps: CellEditProps<T> = {
      value: editValue,
      row,
      column,
      onChange: (value: unknown) => setEditValue(String(value)),
      onCancel: handleCancel,
      onCommit: handleCommit,
    };

    return (
      <TableCell
        sx={{
          minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
          width: cellWidth ? `${cellWidth}px` : undefined,
          maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
          boxSizing: 'border-box',
        }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            {column.editComponent ? (
              column.editComponent(editProps)
            ) : (
              <TextField
                inputRef={inputRef}
                value={editValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEditValue(newValue);
                }}
                onKeyDown={handleKeyDown}
                variant="standard"
                size="small"
                fullWidth
                // Ensure input text is visible with proper styling
                inputProps={{
                  style: {
                    fontSize: '14px',
                    color: '#000000',
                    backgroundColor: '#ffffff',
                    padding: '4px',
                    border: '1px solid #ccc',
                  },
                }}
                sx={{
                  '& .MuiInput-input': {
                    color: '#000000 !important',
                    fontSize: '14px !important',
                    backgroundColor: '#ffffff !important',
                  },
                }}
              />
            )}
            <IconButton size="small" onClick={handleCommit}>
              <SystemIcon
                iconName="Check"
                style={{
                  color: theme.palette.text.opposite,
                  fontSize: '16px',
                }}
              />
            </IconButton>
            <IconButton size="small" onClick={handleCancel}>
              <SystemIcon
                iconName="X"
                style={{
                  color: theme.palette.text.opposite,
                  fontSize: '14px',
                }}
              />
            </IconButton>
          </Box>
        </ClickAwayListener>
      </TableCell>
    );
  }

  // Display mode
  return (
    <TableCell
      ref={cellRef}
      onClick={handleCellClick}
      onDoubleClick={handleCellDoubleClick}
      tabIndex={column.editable ? 0 : -1}
      sx={{
        cursor: column.editable ? 'pointer' : 'default',
        '&:hover': column.editable
          ? {
              backgroundColor: 'action.hover',
            }
          : {},
        // CSS focus styling - always show outline when focused
        '&:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '-2px',
        },
        // Add visual focus indicator for keyboard navigation
        ...(isFocused && {
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(144, 202, 249, 0.12)'
              : 'rgba(25, 118, 210, 0.12)',
          outline: '3px solid',
          outlineColor: 'primary.main',
          outlineOffset: '-3px',
        }),
      }}
    >
      {column.format ? column.format(value, row) : String(value || '')}
    </TableCell>
  );
}
